import React, { useEffect, useState } from "react";
import { Loader2, Plus, Trash2, Save, X, Tag } from "lucide-react";
import { toast } from "sonner";

import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { api, unwrap } from "@/lib/api";

const blank = () => ({
  code: "", description: "",
  discount_type: "percent", discount_value: 10,
  min_subtotal: 0, max_uses: 0,
  valid_from: "", valid_until: "",
  is_active: true,
});

export default function AdminPromos() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(null);

  const load = async () => {
    setLoading(true);
    try {
      const r = await api.get("/admin/promos");
      setItems(unwrap(r) || []);
    } finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const save = async (p) => {
    const body = {
      code: p.code,
      description: p.description,
      discount_type: p.discount_type,
      discount_value: Number(p.discount_value),
      min_subtotal: Number(p.min_subtotal),
      max_uses: Number(p.max_uses),
      valid_from: p.valid_from || null,
      valid_until: p.valid_until || null,
      is_active: !!p.is_active,
    };
    try {
      if (p.id) await api.patch(`/admin/promos/${p.id}`, body);
      else await api.post("/admin/promos", body);
      toast.success("Promo saved");
      setEditing(null);
      load();
    } catch (err) { toast.error(err?.response?.data?.detail?.message || "Save failed"); }
  };

  const remove = async (id) => {
    if (!window.confirm("Delete this promo code?")) return;
    try { await api.delete(`/admin/promos/${id}`); toast.success("Deleted"); load(); }
    catch { toast.error("Delete failed"); }
  };

  if (loading) return <div className="grid place-items-center py-16"><Loader2 className="w-6 h-6 animate-spin" /></div>;

  return (
    <div className="space-y-5" data-testid="admin-promos">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="font-display text-3xl font-extrabold tracking-tighter">Promo codes</h1>
          <p className="text-[#4B5563] text-sm mt-1">{items.length} codes · uses are tracked automatically when applied</p>
        </div>
        <Button onClick={() => setEditing(blank())} className="rounded-xl bg-[#0EA5A4] hover:bg-[#0F766E] text-white" data-testid="add-promo-btn">
          <Plus className="w-4 h-4 mr-1" /> New promo
        </Button>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        {items.length === 0 && (
          <Card className="rounded-2xl p-8 border border-dashed border-[#E5E7EB] text-center text-[#4B5563]">
            <Tag className="w-7 h-7 mx-auto mb-2 text-[#9CA3AF]" />
            No promo codes yet. Create one to offer a discount in the booking wizard.
          </Card>
        )}
        {items.map((p) => (
          <Card key={p.id} className="rounded-2xl p-5 border border-[#E5E7EB]" data-testid={`promo-row-${p.code}`}>
            <div className="flex items-start justify-between gap-3">
              <div>
                <div className="font-mono font-bold text-xl tracking-wider">{p.code}</div>
                <div className="text-sm text-[#4B5563] mt-1">{p.description || "—"}</div>
                <div className="mt-2 flex flex-wrap gap-3 text-xs">
                  <span className="font-mono font-semibold text-[#0EA5A4]">
                    {p.discount_type === "percent" ? `${p.discount_value}% off` : `$${Number(p.discount_value).toFixed(2)} off`}
                  </span>
                  <span className="text-[#9CA3AF]">Used: {p.uses}{p.max_uses ? `/${p.max_uses}` : ""}</span>
                  {p.min_subtotal > 0 && <span className="text-[#9CA3AF]">Min: ${p.min_subtotal}</span>}
                </div>
              </div>
              <div className={`text-[10px] font-bold uppercase tracking-wider ${p.is_active ? "text-emerald-600" : "text-stone-400"}`}>
                {p.is_active ? "Active" : "Inactive"}
              </div>
            </div>
            <div className="mt-4 flex gap-2">
              <Button onClick={() => setEditing({ ...p })} variant="outline" size="sm" className="rounded-xl" data-testid={`edit-promo-${p.code}`}>Edit</Button>
              <Button onClick={() => remove(p.id)} variant="outline" size="sm" className="rounded-xl text-red-500" data-testid={`delete-promo-${p.code}`}>
                <Trash2 className="w-4 h-4" />
              </Button>
            </div>
          </Card>
        ))}
      </div>

      {editing && <PromoEditor data={editing} onSave={save} onCancel={() => setEditing(null)} />}
    </div>
  );
}

function PromoEditor({ data, onSave, onCancel }) {
  const [p, setP] = useState(data);
  const upd = (patch) => setP((s) => ({ ...s, ...patch }));
  return (
    <Card className="rounded-2xl p-6 border border-[#0EA5A4]" data-testid="promo-editor">
      <div className="flex items-center justify-between mb-4">
        <h3 className="font-display font-bold">{p.id ? "Edit promo" : "New promo"}</h3>
        <button onClick={onCancel}><X className="w-5 h-5" /></button>
      </div>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <div>
          <Label>Code (uppercase)</Label>
          <Input value={p.code} onChange={(e) => upd({ code: e.target.value.toUpperCase().replace(/\s+/g, "") })}
            className="rounded-xl mt-1 font-mono uppercase tracking-wider" data-testid="promo-code" />
        </div>
        <div>
          <Label>Description (internal)</Label>
          <Input value={p.description} onChange={(e) => upd({ description: e.target.value })}
            className="rounded-xl mt-1" data-testid="promo-description" />
        </div>
        <div>
          <Label>Discount type</Label>
          <select value={p.discount_type} onChange={(e) => upd({ discount_type: e.target.value })}
            className="mt-1 w-full h-10 rounded-xl border border-[#E5E7EB] px-3 bg-white" data-testid="promo-type">
            <option value="percent">Percent (%)</option>
            <option value="flat">Flat ($)</option>
          </select>
        </div>
        <div>
          <Label>Discount value</Label>
          <Input type="number" step="0.01" value={p.discount_value}
            onChange={(e) => upd({ discount_value: e.target.value })}
            className="rounded-xl mt-1 font-mono" data-testid="promo-value" />
        </div>
        <div>
          <Label>Min subtotal ($, optional)</Label>
          <Input type="number" step="0.01" value={p.min_subtotal}
            onChange={(e) => upd({ min_subtotal: e.target.value })}
            className="rounded-xl mt-1 font-mono" data-testid="promo-min" />
        </div>
        <div>
          <Label>Max uses (0 = unlimited)</Label>
          <Input type="number" value={p.max_uses} onChange={(e) => upd({ max_uses: e.target.value })}
            className="rounded-xl mt-1 font-mono" data-testid="promo-max" />
        </div>
        <div>
          <Label>Valid from (optional)</Label>
          <Input type="date" value={p.valid_from ? p.valid_from.slice(0, 10) : ""}
            onChange={(e) => upd({ valid_from: e.target.value })}
            className="rounded-xl mt-1" data-testid="promo-from" />
        </div>
        <div>
          <Label>Valid until (optional)</Label>
          <Input type="date" value={p.valid_until ? p.valid_until.slice(0, 10) : ""}
            onChange={(e) => upd({ valid_until: e.target.value })}
            className="rounded-xl mt-1" data-testid="promo-until" />
        </div>
        <div className="flex items-center gap-3">
          <Switch checked={!!p.is_active} onCheckedChange={(v) => upd({ is_active: v })} data-testid="promo-active" />
          <Label>Active</Label>
        </div>
      </div>
      <div className="mt-5 flex gap-2">
        <Button onClick={() => onSave(p)} className="rounded-xl bg-[#0EA5A4] hover:bg-[#0F766E] text-white" data-testid="promo-save"><Save className="w-4 h-4 mr-2" /> Save</Button>
        <Button onClick={onCancel} variant="outline" className="rounded-xl" data-testid="promo-cancel">Cancel</Button>
      </div>
    </Card>
  );
}
